/**
 * 이미지 압축 유틸리티
 *
 * Canvas API를 사용한 클라이언트 사이드 이미지 압축
 * Vision API 업로드 전 이미지 최적화에 사용
 *
 * 기능:
 * - 최대 해상도 제한 (긴 쪽 1920px)
 * - JPEG 품질 조정 (목표 크기 달성까지 단계적 감소)
 * - 압축 결과 메타데이터 제공
 */

/**
 * 압축 옵션
 */
export interface CompressOptions {
  /** 최대 너비 (기본값: 1920) */
  maxWidth?: number;

  /** 최대 높이 (기본값: 1920) */
  maxHeight?: number;

  /** 초기 JPEG 품질 (0.0 ~ 1.0, 기본값: 0.85) */
  quality?: number;

  /** 목표 파일 크기 KB (기본값: 1024 = 1MB) */
  targetSizeKB?: number;

  /** 최소 JPEG 품질 (기본값: 0.5) */
  minQuality?: number;
}

/**
 * 압축 결과 메타데이터
 */
export interface CompressionResult {
  /** 원본 파일 크기 (bytes) */
  originalSize: number;

  /** 압축 후 파일 크기 (bytes) */
  compressedSize: number;

  /** 압축률 (0.0 ~ 1.0, 1.0 = 100% 압축) */
  compressionRatio: number;

  /** 최종 이미지 크기 */
  dimensions: {
    width: number;
    height: number;
  };

  /** 원본 이미지 크기 */
  originalDimensions: {
    width: number;
    height: number;
  };

  /** 사용된 JPEG 품질 */
  finalQuality: number;

  /** 압축 소요 시간 (ms) */
  processingTime: number;
}

/**
 * 압축 응답 (압축된 파일 + 메타데이터)
 */
export interface CompressedImage {
  /** 압축된 이미지 파일 */
  file: File;

  /** 압축 결과 메타데이터 */
  result: CompressionResult;
}

// 기본 압축 설정
const DEFAULT_OPTIONS: Required<CompressOptions> = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.85,
  targetSizeKB: 1024, // 1MB
  minQuality: 0.5,
};

/**
 * 이미지 파일을 압축합니다.
 *
 * @param file 원본 이미지 파일
 * @param options 압축 옵션
 * @returns 압축된 이미지와 메타데이터
 *
 * @example
 * ```typescript
 * const { file: compressed, result } = await compressImage(originalFile);
 * console.log(`압축률: ${(result.compressionRatio * 100).toFixed(1)}%`);
 * ```
 */
export async function compressImage(
  file: File,
  options?: CompressOptions
): Promise<CompressedImage> {
  const startTime = performance.now();
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // 원본 이미지 로드
  const img = await loadImage(file);
  const originalDimensions = { width: img.width, height: img.height };

  // 리사이즈 계산
  const { width, height } = calculateDimensions(
    img.width,
    img.height,
    opts.maxWidth,
    opts.maxHeight
  );

  // Canvas에 그리기
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context 생성 실패');
  }

  // 고품질 리사이징 설정
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  // 목표 크기에 맞춰 품질 조정하며 압축
  let quality = opts.quality;
  let blob: Blob | null = null;
  const targetBytes = opts.targetSizeKB * 1024;

  // 품질을 점진적으로 낮추며 목표 크기 달성
  while (quality >= opts.minQuality) {
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);

    if (blob.size <= targetBytes) {
      break;
    }

    // 품질 10% 감소
    quality -= 0.1;
  }

  // 최종 blob이 없으면 최소 품질로 재시도
  if (!blob) {
    blob = await canvasToBlob(canvas, 'image/jpeg', opts.minQuality);
    quality = opts.minQuality;
  }

  // File 객체 생성 (원본 파일명 유지, 확장자만 변경)
  const fileName = file.name.replace(/\.[^.]+$/, '.jpg');
  const compressedFile = new File([blob], fileName, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });

  const endTime = performance.now();

  const result: CompressionResult = {
    originalSize: file.size,
    compressedSize: compressedFile.size,
    compressionRatio:
      file.size > 0 ? 1 - compressedFile.size / file.size : 0,
    dimensions: { width, height },
    originalDimensions,
    finalQuality: Math.round(quality * 100) / 100,
    processingTime: Math.round(endTime - startTime),
  };

  return { file: compressedFile, result };
}

/**
 * 이미지 파일의 크기를 가져옵니다.
 *
 * @param file 이미지 파일
 * @returns 이미지 너비와 높이
 */
export async function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  const img = await loadImage(file);
  return { width: img.width, height: img.height };
}

/**
 * 파일 크기를 사람이 읽기 쉬운 형식으로 변환합니다.
 *
 * @param bytes 바이트 크기
 * @returns 포맷된 문자열 (예: "1.5MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes}B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)}KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  }
}

/**
 * 압축이 필요한지 확인합니다.
 *
 * @param file 이미지 파일
 * @param options 압축 옵션
 * @returns 압축 필요 여부
 */
export async function needsCompression(
  file: File,
  options?: CompressOptions
): Promise<boolean> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const targetBytes = opts.targetSizeKB * 1024;

  // 파일 크기 체크
  if (file.size > targetBytes) {
    return true;
  }

  // 이미지 크기 체크
  try {
    const { width, height } = await getImageDimensions(file);
    return width > opts.maxWidth || height > opts.maxHeight;
  } catch {
    // 이미지 로드 실패 시 압축 시도
    return true;
  }
}

/**
 * 압축 결과를 사용자에게 보여줄 문자열로 포맷합니다.
 *
 * @param result 압축 결과
 * @returns 포맷된 문자열 (예: "5.2MB → 0.8MB (84% 감소)")
 */
export function formatCompressionResult(result: CompressionResult): string {
  const originalStr = formatFileSize(result.originalSize);
  const compressedStr = formatFileSize(result.compressedSize);
  const ratioPercent = Math.round(result.compressionRatio * 100);

  if (ratioPercent <= 0) {
    return `${originalStr} (압축 불필요)`;
  }

  return `${originalStr} → ${compressedStr} (${ratioPercent}% 감소)`;
}

// ============================================================================
// Private Helpers
// ============================================================================

/**
 * 이미지 파일을 HTMLImageElement로 로드합니다.
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('이미지 로드 실패'));
    };

    img.src = url;
  });
}

/**
 * 최대 크기에 맞는 새 크기를 계산합니다.
 * 비율을 유지하면서 maxWidth/maxHeight 내로 조정
 */
function calculateDimensions(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  // 이미 작으면 그대로 반환
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  // 비율 유지하며 축소
  const ratio = Math.min(maxWidth / width, maxHeight / height);

  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

/**
 * Canvas를 Blob으로 변환합니다.
 */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Blob 변환 실패'));
        }
      },
      type,
      quality
    );
  });
}
